/**
 * Regulations Engine - Main Entry Point
 * Canonical source for all regulatory requirements
 */

import { useEffect, useState, useMemo } from 'react';
import { useVessel } from 'contexts/VesselContext';
import { generateDailyCheckRequirement } from './helpers/dailyChecks';

/**
 * Version of the regulations ruleset
 * Update when regulations change
 */
export const RULESET_VERSION = '2025.08.29';

/**
 * All regulatory rules
 * This is the single source of truth for requirements
 */
const RULES = [
  // Daily Checks Rule (operations module)
  {
    id: 'rule_daily_checks',
    module: 'operations',
    evaluate: (ctx) => {
      if (ctx.module !== 'operations') return null;
      return generateDailyCheckRequirement(ctx);
    }
  },

  // Stub Maintenance Rule
  {
    id: 'rule_maintenance_stub',
    module: 'maintenance',
    evaluate: (ctx) => {
      if (ctx.module !== 'maintenance') return null;
      
      // Placeholder - will be replaced with real maintenance requirements
      return {
        id: 'maintenance_monthly_checks',
        module: 'maintenance',
        name: 'Monthly Equipment Checks',
        description: 'Placeholder for monthly equipment maintenance checks',
        required: false,
        status: 'recommended',
        citation: 'WBC3 Section 14.2 (placeholder)',
        frequency: 'monthly',
        conditions: []
      };
    }
  },

  // Stub Drills Rule
  {
    id: 'rule_drills_stub',
    module: 'drills',
    evaluate: (ctx) => {
      if (ctx.module !== 'drills') return null;
      
      // Placeholder - will be replaced with real drill requirements
      return {
        id: 'drills_fire_weekly',
        module: 'drills',
        name: 'Weekly Fire Drill',
        description: 'Placeholder for weekly fire drill requirement',
        required: false,
        status: 'recommended',
        citation: 'MGN 71 Section 3.1 (placeholder)',
        frequency: 'weekly',
        conditions: []
      };
    }
  }
];

/**
 * Evaluate all requirements for a given context
 * This is the main engine function
 */
export function evaluateRequirements(ctx) {
  const requirements = [];
  
  for (const rule of RULES) {
    // Only evaluate rules for the requested module
    if (rule.module === ctx.module) {
      const requirement = rule.evaluate(ctx);
      if (requirement) {
        requirements.push(requirement);
      }
    }
  }
  
  return requirements;
}

/**
 * React hook to get requirements for a module
 * Automatically uses current vessel from context
 */
export function useRequirements(module, date) {
  const { currentVessel } = useVessel(); // Use stable currentVessel from context
  const [requirements, setRequirements] = useState([]);
  
  // Memoize the context to avoid unnecessary re-evaluations
  const context = useMemo(() => {
    if (!currentVessel) return null;
    
    return {
      vessel: {
        id: currentVessel.id,
        name: currentVessel.name,
        type: currentVessel.type || currentVessel.vesselType, // Support both field names
        category: currentVessel.category,
        flag: currentVessel.flag,
        operations: currentVessel.operations || [],
        equipment: currentVessel.equipment || [],
        hasPassengers: currentVessel.hasPassengers,
        operatingStatus: currentVessel.operatingStatus
      },
      module,
      date: date || new Date().toISOString().split('T')[0]
    };
  }, [currentVessel, module, date]); // currentVessel is now stable after Fix #1
  
  // Evaluate requirements when context changes
  useEffect(() => {
    if (!context) {
      setRequirements([]);
      return;
    }
    
    const reqs = evaluateRequirements(context);
    setRequirements(reqs);
  }, [context]);
  
  return requirements;
}

/**
 * Get requirements without React hook (for non-React code)
 */
export function getRequirements(vessel, module, date) {
  const context = {
    vessel,
    module,
    date: date || new Date().toISOString().split('T')[0]
  };
  
  return evaluateRequirements(context);
}

/**
 * Check if a specific requirement is active
 */
export function isRequirementActive(vessel, requirementId, module) {
  const requirements = getRequirements(vessel, module);
  const requirement = requirements.find(r => r.id === requirementId);
  return requirement?.required === true;
}

/**
 * Export metadata about the engine
 */
export const ENGINE_METADATA = {
  version: RULESET_VERSION,
  modules: ['operations', 'maintenance', 'drills', 'crew', 'defects'],
  ruleCount: RULES.length
};